import { Observable, Subscription, Subject, EMPTY, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  HostListener,
  viewChild,
  ElementRef,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  AppRoutes,
  FundService,
  FundTypes,
  SearchService,
  LanguageService,
  ProfilePictureService,
  GeneralService,
} from '@gs-2.0/core';

import { DropdownItem } from '../dropdown/dropdown.component';
import { calculateDropdownPosition } from '../../utils/dropdown-position.util';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  // Expose AppRoutes enum to template
  readonly AppRoutes = AppRoutes;

  isAuthenticated$!: Observable<boolean>;
  userFullName: string | null = null;

  // Profile picture URL with default fallback
  profilePictureUrl: string = 'https://fdi-files.s3.amazonaws.com/public/userAvatar.jpg';
  private readonly defaultProfilePicture =
    'https://fdi-files.s3.amazonaws.com/public/userAvatar.jpg';

  logoUrl: string = '';

  // Fund management dropdown state
  isFundDropdownOpen = signal<boolean>(false);
  fundDropdownOpenAbove = signal<boolean>(false);
  private fundDropdownCloseTimer: ReturnType<typeof setTimeout> | null = null;

  // Profile submenu state
  isProfileSubmenuOpen = signal<boolean>(false);

  // Mobile menu state
  isMobileMenuOpen = signal<boolean>(false);
  isFundManagementOpen = signal<boolean>(false);
  isMobileSearchOpen = signal<boolean>(false);
  expandedMobileItemId = signal<string | null>(null);

  // Mobile search recommendations state
  mobileSearchValue = signal<string>('');
  isMobileSearchFocused = signal<boolean>(false);
  private mobileSearchSubject = new Subject<string>();

  // Flag to prevent ghost clicks on iOS when opening submenu
  private preventGhostClick = false;

  // Reference to the fund management wrapper element
  fundManagementWrapper = viewChild<ElementRef>('fundManagementWrapper');

  // Search recommendations state
  searchValue = signal<string>('');
  isSearchFocused = signal<boolean>(false);
  searchRecommendations = signal<any>({ charities: [], projects: [] });
  isLoadingRecommendations = signal<boolean>(false);
  private searchSubject = new Subject<string>();

  // Subscription for cleanup
  private authSubscription?: Subscription;
  private searchSubscription?: Subscription;
  private mobileSearchSubscription?: Subscription;
  private profilePictureSubscription?: Subscription;
  private userNameSubscription?: Subscription;

  // Fund management dropdown items — computed based on selected fund type
  fundManagementItems = computed<DropdownItem[]>(() => {
    const fundType = this.fundService.fundType();
    const isProject = fundType === FundTypes.PROJECT;
    const isVendor =
      fundType === FundTypes.CHARITY ||
      fundType === FundTypes.PROJECT ||
      fundType === FundTypes.PROJECT_CHARITY;
    const isTransferEligible = this.fundService.isTransferEligible();
    const isFullAccess = this.fundService.isFullAccess();
    const isExpired = this.fundService.isExpiredFund();

    const items: DropdownItem[] = [
      { id: 'dashboard', label: 'nav.fund_menu.dashboard', data: AppRoutes.FUND_DASHBOARD },
    ];

    if (!isExpired) {
      items.push({
        id: 'contribute',
        label: 'nav.fund_menu.contribute',
        data: AppRoutes.FUND_DONATE,
      });
    }

    if (!isExpired && !isProject && isFullAccess) {
      if (isVendor) {
        items.push({
          id: 'distribute',
          label: 'nav.fund_menu.distribute',
          data: AppRoutes.FUND_DISTRIBUTE,
          children: [
            {
              id: 'manage-recurring-distributions',
              label: 'nav.fund_menu.manage_recurring_distributions',
              data: AppRoutes.FUND_MANAGE_RECURRING,
              queryParams: { type: 'distribution' },
            },
          ],
        });
      } else {
        items.push({
          id: 'grant',
          label: 'nav.fund_menu.grant',
          data: AppRoutes.FUND_GRANT,
          children: [
            {
              id: 'manage-recurring-grants',
              label: 'nav.fund_menu.manage_recurring_grants',
              data: AppRoutes.FUND_MANAGE_RECURRING,
              queryParams: { type: 'grant' },
            },
          ],
        });
      }
    }

    if (!isExpired && isTransferEligible) {
      items.push({
        id: 'transfer',
        label: 'nav.fund_menu.transfer',
        data: AppRoutes.FUND_TRANSFER,
      });
    }

    if (!isExpired && !isProject && isFullAccess) {
      items.push({
        id: 'currency-conversion',
        label: 'nav.fund_menu.currency_conversion',
        data: AppRoutes.FUND_CURRENCY_CONVERSION,
      });
    }

    items.push({ id: 'activity', label: 'nav.fund_menu.activity', data: AppRoutes.FUND_ACTIVITY });

    return items;
  });

  // Whether user has portal access (shows "Manage My Fund" dropdown)
  hasPortalAccess = signal<boolean>(false);
  // Whether user is portal admin only (no GS roles, no funds)
  isPortalAdminOnly = signal<boolean>(false);
  // Whether user has any attached funds
  hasFunds = computed(() => this.fundService.funds().length > 0);
  // Guard: ensure header bootstraps fund loading at most once per tab
  private hasTriggeredFundLoad = false;

  isLangDropdownOpen!: boolean;

  constructor(
    private authService: AuthService,
    private router: Router,
    private fundService: FundService,
    private searchService: SearchService,
    private languageService: LanguageService,
    private profilePictureService: ProfilePictureService,
    private generalService: GeneralService
  ) {}

  ngOnInit(): void {
    this.logoUrl = this.resolveWhiteLogoUrl();
    this.isAuthenticated$ = this.authService.isAuthenticated$;
    if (this.authService.hasValidToken()) {
      this.userFullName = this.authService.userFullName;
      // Initialize user name from token
      this.authService.initializeUserNameFromToken(this.authService.userFullName);
      // Initialize profile picture from token
      this.authService.initializeUserProfilePictureFromToken();
      // Initialize profile picture service with the value from auth service
      const userProfilePicture = this.authService.userProfilePicture;
      this.profilePictureService.initializeFromToken(userProfilePicture);
      // Set portal access flags
      this.hasPortalAccess.set(this.authService.hasPortalAccess());
      this.isPortalAdminOnly.set(this.authService.isPortalAdminOnly());
      this.bootstrapFundsForHeader();
    }

    // Subscribe to profile picture changes from the shared service
    // This ensures the header updates when the profile picture is changed in settings
    // Portal-only users have no GS profile — always show the default avatar
    this.profilePictureSubscription = this.profilePictureService.profilePicture$.subscribe(
      (url) => {
        this.profilePictureUrl =
          this.authService.getAppType() === 'PORTAL' ? this.defaultProfilePicture : url;
      }
    );

    // Subscribe to user name changes from the shared service
    // This ensures the header updates when the name is changed in settings
    this.userNameSubscription = this.authService.userFullName$.subscribe((name) => {
      this.userFullName = name;
    });

    // Update profile picture, user name, and access flags whenever authentication state changes
    this.authSubscription = this.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated && this.authService.hasValidToken()) {
        this.userFullName = this.authService.userFullName;
        this.authService.initializeUserNameFromToken(this.authService.userFullName);
        // Initialize profile picture from token
        this.authService.initializeUserProfilePictureFromToken();
        const userProfilePicture = this.authService.userProfilePicture;
        this.profilePictureService.initializeFromToken(userProfilePicture);
        // Update portal access flags
        this.hasPortalAccess.set(this.authService.hasPortalAccess());
        this.isPortalAdminOnly.set(this.authService.isPortalAdminOnly());
        this.bootstrapFundsForHeader();
      } else {
        this.hasPortalAccess.set(false);
        this.isPortalAdminOnly.set(false);
        this.hasTriggeredFundLoad = false;
      }
    });

    // Set up search recommendations with debounce
    // API call is triggered after user stops typing for 400ms
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(400),
        switchMap((searchText) => {
          if (searchText.length >= 3) {
            // Set loading state before making API call
            this.isLoadingRecommendations.set(true);
            const languageCode = this.languageService.getCurrentLanguage().code || 'eng';
            return this.searchService
              .getSearchSuggestions({
                searchText,
                languageCode,
              })
              .pipe(
                catchError(() => {
                  // Handle error - return empty result instead of failing
                  return of({ response: null });
                })
              );
          } else {
            // Return empty result for searches less than 3 characters
            return of({ response: null, tooShort: true });
          }
        }),
        tap((response: any) => {
          // Handle the response
          if (response.tooShort) {
            // Reset state for searches less than 3 characters
            this.searchRecommendations.set({ charities: [], projects: [] });
            this.isLoadingRecommendations.set(false);
          } else if (response && response.response) {
            // Process and set results
            const charities = (response.response.ORGANISATION || [])
              .slice(0, 5)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            const projects = (response.response.PROJECT || [])
              .slice(0, 5)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            this.searchRecommendations.set({ charities, projects });
            this.isLoadingRecommendations.set(false);
          } else {
            // No results
            this.searchRecommendations.set({ charities: [], projects: [] });
            this.isLoadingRecommendations.set(false);
          }
        })
      )
      .subscribe();

    // Set up mobile search recommendations with debounce
    // API call is triggered after user stops typing for 400ms
    this.mobileSearchSubscription = this.mobileSearchSubject
      .pipe(
        debounceTime(400),
        switchMap((searchText) => {
          if (searchText.length >= 3) {
            // Set loading state before making API call
            this.isLoadingRecommendations.set(true);
            const languageCode = this.languageService.getCurrentLanguage().code || 'eng';
            return this.searchService
              .getSearchSuggestions({
                searchText,
                languageCode,
              })
              .pipe(
                catchError(() => {
                  // Handle error - return empty result instead of failing
                  return of({ response: null });
                })
              );
          } else {
            // Return empty result for searches less than 3 characters
            return of({ response: null, tooShort: true });
          }
        }),
        tap((response: any) => {
          // Handle the response
          if (response.tooShort) {
            // Reset state for searches less than 3 characters
            this.searchRecommendations.set({ charities: [], projects: [] });
            this.isLoadingRecommendations.set(false);
          } else if (response && response.response) {
            // Process and set results
            const charities = (response.response.ORGANISATION || [])
              .slice(0, 5)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            const projects = (response.response.PROJECT || [])
              .slice(0, 5)
              .sort((a: any, b: any) => a.name.localeCompare(b.name));
            this.searchRecommendations.set({ charities, projects });
            this.isLoadingRecommendations.set(false);
          } else {
            // No results
            this.searchRecommendations.set({ charities: [], projects: [] });
            this.isLoadingRecommendations.set(false);
          }
        })
      )
      .subscribe();
  }

  /**
   * Ensures the header has fund data so the "Manage My Fund" gate can resolve
   * correctly even when the user lands on a public page that does not load funds
   * itself (e.g. arriving from the TrustBridge site to /home or /organizations).
   * Without this, hasFunds() stays false in fresh tabs because sessionStorage —
   * the only fallback for cached funds — is per-tab.
   *
   * The check is deferred via queueMicrotask so route components (e.g.
   * fund-dashboard) get a chance to call loadFunds() in their own ngOnInit
   * first. We then skip if a load is already in flight or has populated funds,
   * which avoids a duplicate /user-details API call on fund-page cold starts.
   */
  private bootstrapFundsForHeader(): void {
    if (this.hasTriggeredFundLoad) return;
    if (!this.hasPortalAccess() || this.isPortalAdminOnly()) return;

    queueMicrotask(() => {
      if (this.hasTriggeredFundLoad) return;
      if (!this.hasPortalAccess() || this.isPortalAdminOnly()) return;
      if (this.fundService.funds().length > 0) return;
      if (this.fundService.isLoading()) return;

      this.hasTriggeredFundLoad = true;
      this.fundService.loadFunds().catch(() => {
        // Header has no UI to surface this; page-level callers retain their own error handling.
        this.hasTriggeredFundLoad = false;
      });
    });
  }

  private resolveWhiteLogoUrl(): string {
    const settings = this.generalService.settings;
    if (!settings) return '';
    const item = settings.response.globalConfig.find((c) => c.key === 'whiteLogo');
    if (!item) return '';
    try {
      const parsed = JSON.parse(item.value);
      return parsed?.url || '';
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.mobileSearchSubscription?.unsubscribe();
    this.profilePictureSubscription?.unsubscribe();
    this.userNameSubscription?.unsubscribe();
    if (this.fundDropdownCloseTimer) {
      clearTimeout(this.fundDropdownCloseTimer);
    }
  }

  onLoginClick(): void {
    // Trigger Keycloak login directly, redirecting to role-based landing page
    this.authService.login(this.authService.getLandingRoute());
  }

  onLogoutClick(): void {
    // Prevent ghost clicks on iOS from triggering logout
    if (this.preventGhostClick) {
      return;
    }
    this.authService.logout();
  }

  onLogoClick(): void {
    this.router.navigate([AppRoutes.HOME]);
  }

  // Fund management dropdown methods
  onFundManagementMouseEnter(): void {
    if (this.fundDropdownCloseTimer) {
      clearTimeout(this.fundDropdownCloseTimer);
      this.fundDropdownCloseTimer = null;
    }
    this.checkFundDropdownPosition();
    this.isFundDropdownOpen.set(true);
  }

  onFundManagementMouseLeave(): void {
    this.fundDropdownCloseTimer = setTimeout(() => {
      this.isFundDropdownOpen.set(false);
      this.fundDropdownCloseTimer = null;
    }, 100);
  }

  /**
   * Checks if fund dropdown should open above or below based on available space
   */
  private checkFundDropdownPosition(): void {
    const dropdownHeight = 400; // maxHeight is 400px
    const shouldOpenAbove = calculateDropdownPosition(this.fundManagementWrapper(), dropdownHeight);
    this.fundDropdownOpenAbove.set(shouldOpenAbove);
  }

  onFundMenuItemSelected(item: DropdownItem): void {
    // Navigate to the route stored in item.data
    if (item.data) {
      this.router.navigate([item.data], item.queryParams ? { queryParams: item.queryParams } : {});
    }
    this.isFundDropdownOpen.set(false);
  }

  toggleProfileSubmenu(event: MouseEvent): void {
    // Stop propagation to prevent document click handler from immediately closing
    event.stopPropagation();

    const wasOpen = this.isProfileSubmenuOpen();
    this.isProfileSubmenuOpen.update((value) => !value);

    // If we just opened the menu, set a flag to prevent ghost clicks on iOS
    if (!wasOpen && this.isProfileSubmenuOpen()) {
      this.preventGhostClick = true;
      // Clear the flag after a short delay (iOS ghost clicks typically occur within 300-400ms)
      setTimeout(() => {
        this.preventGhostClick = false;
      }, 400);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideProfile = target.closest('.profile-wrapper');
    const clickedInsideMobileMenu = target.closest('.mobile-menu-wrapper');

    if (!clickedInsideProfile && this.isProfileSubmenuOpen()) {
      this.isProfileSubmenuOpen.set(false);
    }

    if (!clickedInsideMobileMenu && this.isMobileMenuOpen()) {
      this.isMobileMenuOpen.set(false);
    }
  }

  onSettingsClick(): void {
    console.log('[Header] Settings clicked');
    // Prevent ghost clicks on iOS from triggering navigation
    if (this.preventGhostClick) {
      console.log('[Header] Prevented ghost click');
      return;
    }
    this.isProfileSubmenuOpen.set(false);
    console.log('[Header] Navigating to settings:', AppRoutes.SETTINGS);
    this.router.navigate([AppRoutes.SETTINGS]);
    console.log('[Header] Navigation called');
  }

  onImageError(event: Event): void {
    // Fallback to default profile picture if image fails to load
    const img = event.target as HTMLImageElement;
    img.src = this.defaultProfilePicture;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen.update((value) => !value);
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
    this.isFundManagementOpen.set(false);
    this.expandedMobileItemId.set(null);
  }

  toggleFundManagement(): void {
    this.isFundManagementOpen.update((value) => !value);
  }

  toggleMobileItem(itemId: string): void {
    this.expandedMobileItemId.update((current) => (current === itemId ? null : itemId));
  }

  toggleMobileSearch(): void {
    this.isMobileSearchOpen.set(true);
    this.isMobileSearchFocused.set(true);
  }

  onMobileSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    this.mobileSearchValue.set(value);

    // Just emit the value - the observable chain handles loading state and API calls
    this.mobileSearchSubject.next(value);
  }

  onMobileSearchFocus(): void {
    this.isMobileSearchFocused.set(true);
  }

  onMobileSearchBlur(): void {
    // Delay hiding recommendations to allow clicking on them
    setTimeout(() => {
      this.isMobileSearchFocused.set(false);
      // Only close if there's no value
      if (!this.mobileSearchValue()) {
        this.isMobileSearchOpen.set(false);
      }
    }, 200);
  }

  clearMobileSearch(): void {
    this.mobileSearchValue.set('');
    this.searchRecommendations.set({ charities: [], projects: [] });
    this.isMobileSearchFocused.set(false);
    this.isMobileSearchOpen.set(false);
    this.isLoadingRecommendations.set(false);
  }

  onGlobalSearchSubmit(searchValue: string): void {
    if (searchValue && searchValue.trim().length >= 3) {
      this.router.navigate([AppRoutes.SEARCH], {
        queryParams: { q: searchValue.trim() },
      });
    }
  }

  onDesktopSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const inputElement = event.target as HTMLInputElement;
      this.onGlobalSearchSubmit(inputElement.value);
    }
  }

  onMobileSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const inputElement = event.target as HTMLInputElement;
      this.onGlobalSearchSubmit(inputElement.value);
      this.clearMobileSearch();
    }
  }

  onMobileRecommendationClick(item: any, type: 'charity' | 'project'): void {
    const languageCode = this.languageService.getCurrentLanguage().code || 'en';
    if (type === 'charity') {
      this.router.navigate([`/charity/${item.uuid}/${languageCode}/summary`]);
    } else if (type === 'project') {
      this.router.navigate([`/project/${item.uuid}/${languageCode}/summary`]);
    }
    this.clearMobileSearch();
  }

  showMobileRecommendations(): boolean {
    return (
      this.isMobileSearchFocused() &&
      this.mobileSearchValue().length >= 3 &&
      (this.searchRecommendations().charities.length > 0 ||
        this.searchRecommendations().projects.length > 0)
    );
  }

  showMobileMinCharactersNote(): boolean {
    return (
      this.isMobileSearchFocused() &&
      this.mobileSearchValue().length > 0 &&
      this.mobileSearchValue().length < 3
    );
  }

  showMobileNoResults(): boolean {
    return (
      this.isMobileSearchFocused() &&
      this.mobileSearchValue().length >= 3 &&
      !this.isLoadingRecommendations() &&
      this.searchRecommendations().charities.length === 0 &&
      this.searchRecommendations().projects.length === 0
    );
  }

  // Search recommendations methods
  onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    this.searchValue.set(value);

    // Just emit the value - the observable chain handles loading state and API calls
    this.searchSubject.next(value);
  }

  onSearchFocus(): void {
    this.isSearchFocused.set(true);
  }

  onSearchBlur(): void {
    // Delay hiding recommendations to allow clicking on them
    setTimeout(() => {
      this.isSearchFocused.set(false);
    }, 200);
  }

  clearSearch(): void {
    this.searchValue.set('');
    this.searchRecommendations.set({ charities: [], projects: [] });
    this.isSearchFocused.set(false);
    this.isLoadingRecommendations.set(false);
  }

  onRecommendationClick(item: any, type: 'charity' | 'project'): void {
    const languageCode = this.languageService.getCurrentLanguage().code || 'en';
    if (type === 'charity') {
      this.router.navigate([`/charity/${item.uuid}/${languageCode}/summary`]);
    } else if (type === 'project') {
      this.router.navigate([`/project/${item.uuid}/${languageCode}/summary`]);
    }
    this.clearSearch();
  }

  showRecommendations(): boolean {
    return (
      this.isSearchFocused() &&
      this.searchValue().length >= 3 &&
      (this.searchRecommendations().charities.length > 0 ||
        this.searchRecommendations().projects.length > 0)
    );
  }

  showMinCharactersNote(): boolean {
    return this.isSearchFocused() && this.searchValue().length > 0 && this.searchValue().length < 3;
  }

  showNoResults(): boolean {
    return (
      this.isSearchFocused() &&
      this.searchValue().length >= 3 &&
      !this.isLoadingRecommendations() &&
      this.searchRecommendations().charities.length === 0 &&
      this.searchRecommendations().projects.length === 0
    );
  }
}
